"use client";

import { useState } from "react";
import Link from "next/link";

type Role = "patient" | "clinic";

export default function SignUpInteractive() {
  const [mode, setMode] = useState<"signup" | "signin">("signup");
  const [role, setRole] = useState<Role>("patient");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    // Backend connection point: connect to auth service (e.g., Supabase auth)
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-20" style={{ background: "#FDF8F4" }}>
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto"
            style={{ background: "linear-gradient(135deg, #D4EAD9, #EBF4FA)" }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#5A8A6F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div className="space-y-2">
            <h2 className="font-heading font-bold text-2xl text-ink">
              {mode === "signup" ? "Welcome to BabyBank! 💛" : "Welcome back! 💛"}
            </h2>
            <p className="text-ink-muted">
              {mode === "signup" ?"Check your email to verify your account, then start your campaign." :"You're signed in. Ready to continue your journey?"}
            </p>
          </div>
          <Link href="/create-campaign" className="btn-primary w-full justify-center">
            {role === "patient" ? "Create Your Campaign" : "Set Up Clinic Dashboard"}
          </Link>
          <Link href="/homepage" className="block text-sm text-ink-muted hover:text-primary transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: "#FDF8F4" }}>
      {/* Left decorative panel — desktop only */}
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden"
        style={{ background: "linear-gradient(160deg, #1E4D6B 0%, #2D6A8F 50%, #3D6B52 100%)" }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-80 h-80 rounded-full"
            style={{ background: "radial-gradient(ellipse, rgba(232,165,152,0.25) 0%, transparent 70%)", transform: "translate(30%,-30%)" }} />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full"
            style={{ background: "radial-gradient(ellipse, rgba(255,255,255,0.10) 0%, transparent 70%)", transform: "translate(-30%,30%)" }} />
          <svg className="absolute right-8 bottom-32 opacity-10" width="180" height="180" viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <circle cx="100" cy="100" r="80" stroke="white" strokeWidth="1"/>
            <circle cx="100" cy="100" r="50" stroke="white" strokeWidth="1"/>
            {[0,60,120,180,240,300].map((deg) => (
              <ellipse key={deg} cx="100" cy="100" rx="10" ry="28" stroke="white" strokeWidth="0.8"
                transform={`rotate(${deg} 100 100) translate(0 -50)`} />
            ))}
          </svg>
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link href="/homepage" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)" }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 3C10 3 6 5.5 6 9.5C6 11.985 7.79 14 10 14C12.21 14 14 11.985 14 9.5C14 5.5 10 3 10 3Z" fill="white" fillOpacity="0.9"/>
                <path d="M7 13.5C5.5 14.5 4.5 16 4.5 17.5H15.5C15.5 16 14.5 14.5 13 13.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <span className="font-heading font-bold text-xl text-white">BabyBank</span>
          </Link>

          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="font-heading font-bold text-4xl text-white leading-tight" style={{ letterSpacing: "-0.02em" }}>
                Your journey toward parenthood starts here.
              </h2>
              <p className="text-white/70 leading-relaxed">
                Join thousands of families who've funded their fertility treatments through the love and support of their community.
              </p>
            </div>

            <div className="p-6 rounded-2xl" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.15)" }}>
              <p className="text-white/90 text-sm leading-relaxed italic mb-4">
                "We never thought we could afford a second IVF cycle. BabyBank made it possible — and we're now holding our daughter."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs text-white flex-shrink-0"
                  style={{ background: "#E8A598" }}>A</div>
                <div>
                  <p className="text-white text-xs font-semibold">Amara & David Osei</p>
                  <p className="text-white/50 text-xs">IVF · Minneapolis, MN</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {[
                { val: "2,800+", label: "Campaigns" },
                { val: "$12.4M", label: "Raised" },
                { val: "94%", label: "Goal Rate" },
              ].map(({ val, label }) => (
                <div key={label} className="text-center">
                  <p className="font-heading font-bold text-2xl text-white">{val}</p>
                  <p className="text-white/50 text-xs mt-0.5">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-5">
            {["🔒 Private", "✨ Free to Start", "🏥 Clinic-Backed"].map((item) => (
              <span key={item} className="text-xs text-white/60 font-medium">{item}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Form panel */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 py-12 pt-24 lg:pt-12 overflow-y-auto">
        <div className="w-full max-w-md space-y-7">
          {/* Header */}
          <div className="space-y-1.5">
            <h1 className="font-heading font-bold text-2xl text-ink">
              {mode === "signup" ? "Create your account" : "Welcome back"}
            </h1>
            <p className="text-sm text-ink-muted">
              {mode === "signup" ? "Already have an account? " : "Don't have an account? "}
              <button
                onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
                className="text-primary font-semibold hover:underline transition-colors"
              >
                {mode === "signup" ? "Sign in" : "Sign up free"}
              </button>
            </p>
          </div>

          {/* Role selector — signup only */}
          {mode === "signup" && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-ink block">I am a…</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: "patient" as Role, label: "Patient / Family", icon: "💛", desc: "Creating a campaign" },
                  { id: "clinic" as Role, label: "Fertility Clinic", icon: "🏥", desc: "Partnering with BabyBank" },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setRole(r.id)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all duration-200 ${
                      role === r.id
                        ? "border-primary bg-primary-pale" :"border-warm-border bg-white hover:border-primary/40"
                    }`}
                  >
                    <span className="text-2xl mb-1.5 block" aria-hidden="true">{r.icon}</span>
                    <p className="font-heading font-semibold text-sm text-ink">{r.label}</p>
                    <p className="text-xs text-ink-muted mt-0.5">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Google OAuth */}
          <button
            type="button"
            className="w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl border-2 border-warm-border bg-white hover:border-primary/40 hover:shadow-warm-sm transition-all duration-200 text-sm font-semibold text-ink"
            onClick={() => {
              // Backend connection point: trigger Google OAuth via Supabase or NextAuth
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: "#EDE5DB" }} />
            <span className="text-xs text-ink-soft font-medium">or continue with email</span>
            <div className="flex-1 h-px" style={{ background: "#EDE5DB" }} />
          </div>

          {/* Email / password form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full name — signup only */}
            {mode === "signup" && (
              <div className="space-y-1.5">
                <label htmlFor="full-name" className="text-sm font-medium text-ink">
                  {role === "patient" ? "Your full name" : "Contact person name"}
                </label>
                <input
                  id="full-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={role === "patient" ? "e.g., Sarah Johnson" : "e.g., Dr. Emily Park"}
                  required
                  className="input-field"
                  autoComplete="name"
                />
              </div>
            )}

            {/* Clinic name — signup + clinic role */}
            {mode === "signup" && role === "clinic" && (
              <div className="space-y-1.5">
                <label htmlFor="clinic-name" className="text-sm font-medium text-ink">Clinic name</label>
                <input
                  id="clinic-name"
                  type="text"
                  value={clinicName}
                  onChange={(e) => setClinicName(e.target.value)}
                  placeholder="e.g., Twin Cities Fertility Center"
                  required
                  className="input-field"
                />
              </div>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-ink">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="input-field"
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label htmlFor="password" className="text-sm font-medium text-ink">Password</label>
                {mode === "signin" && (
                  <a href="#" className="text-xs text-primary hover:underline font-medium">Forgot password?</a>
                )}
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === "signup" ? "Create a strong password" : "Enter your password"}
                  required
                  className="input-field pr-12"
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-soft hover:text-ink transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {mode === "signup" && (
                <p className="text-xs text-ink-soft">Minimum 8 characters. Use a mix of letters and numbers.</p>
              )}
            </div>

            {/* Password strength indicator — signup only */}
            {mode === "signup" && password.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((level) => {
                    const strength = Math.min(
                      Math.floor(password.length / 3) +
                      (password.match(/[A-Z]/) ? 1 : 0) +
                      (password.match(/[0-9]/) ? 1 : 0) +
                      (password.match(/[^A-Za-z0-9]/) ? 1 : 0),
                      4
                    );
                    const colors = ["#E8A598", "#D4806F", "#5A8A6F", "#3D6B52"];
                    return (
                      <div
                        key={level}
                        className="flex-1 h-1 rounded-full transition-all duration-300"
                        style={{ background: level <= strength ? colors[strength - 1] : "#EDE5DB" }}
                      />
                    );
                  })}
                </div>
                <p className="text-xs text-ink-soft">
                  {password.length < 6 ? "Too short" :
                   password.length < 10 ? "Could be stronger" :
                   password.match(/[A-Z]/) && password.match(/[0-9]/) ? "Strong password ✓" : "Good password"}
                </p>
              </div>
            )}

            {/* Terms — signup only */}
            {mode === "signup" && (
              <div className="flex items-start gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setAgreeTerms(!agreeTerms)}
                  className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 flex items-center justify-center border-2 transition-all duration-200 ${
                    agreeTerms ? "bg-primary border-primary" : "border-warm-border bg-white"
                  }`}
                  aria-checked={agreeTerms}
                  role="checkbox"
                  aria-label="Agree to terms and privacy policy"
                >
                  {agreeTerms && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
                <p className="text-xs text-ink-muted leading-relaxed">
                  I agree to BabyBank's{" "}
                  <a href="#" className="text-primary hover:underline font-medium">Terms of Service</a>{" "}
                  and{" "}
                  <a href="#" className="text-primary hover:underline font-medium">Privacy Policy</a>.
                  Your data is always kept private and secure.
                </p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={mode === "signup" && !agreeTerms}
              className="btn-primary w-full justify-center text-base py-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
            >
              {mode === "signup"
                ? role === "patient" ?"Create My Account" :"Register Our Clinic" :"Sign In"}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </form>

          {/* Bottom: privacy note */}
          <div className="p-4 rounded-2xl text-center" style={{ background: "#F5EFE8", border: "1px solid #EDE5DB" }}>
            <p className="text-xs text-ink-muted leading-relaxed">
              🔒 Your account and any campaigns you create are{" "}
              <strong className="text-ink">completely private</strong> by default.
              We never share your information with third parties.
            </p>
          </div>

          {/* Sign up CTA for donors */}
          <div className="text-center pt-2">
            <p className="text-xs text-ink-soft">
              Received a donation link from a friend?{" "}
              <a href="#" className="text-primary font-semibold hover:underline">No account needed to donate.</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}